/**
 * security-event.service.js — Security events domain service.
 * Correlated events, incidents, alert correlations, intent analysis.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
} from './_core';

export async function getSecurityEvents(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/security-events/${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Security events fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI security events');
}

export async function getSecurityEventDetail(eventId, token = null) {
  const safeEventId = Number(eventId);
  if (!Number.isFinite(safeEventId)) throw new Error('Valid event id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/security-events/${encodeURIComponent(String(safeEventId))}/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Security event detail failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI security event detail');
}

export async function createSecurityIncident(payload, token = null) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('A valid incident payload object is required.');
  }

  const response = await fetchWithAuthRetry(
    toApiUrl('/ai/security-events/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(payload),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Security incident creation failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI security incident create');
}

export async function getAlertCorrelations(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/alerts/correlate/${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Alert correlations fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI alert correlations');
}

export async function getIntentAnalysis(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/intent-analysis/${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Intent analysis fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI intent analysis');
}

export async function explainSecurityEvent(eventId, token = null) {
  const safeEventId = Number(eventId);
  if (!Number.isFinite(safeEventId)) throw new Error('Valid event_id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl('/ai/explain-event/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify({ event_id: safeEventId }),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Event explanation failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI explain event');
}

export async function triggerSecurityEventGeneration(payload = null, token = null) {
  const body = {
    lookback_hours: Number(payload?.lookback_hours) > 0 ? Number(payload.lookback_hours) : 24,
  };

  const response = await fetchWithAuthRetry(
    toApiUrl('/ai/security-events/generate/'),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(body),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Security event generation trigger failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI security event generation trigger');
}

export async function getSecurityEventGenerationStatus(taskId, token = null) {
  const safeTaskId = String(taskId || '').trim();
  if (!safeTaskId) throw new Error('Valid task_id is required.');

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/security-events/generate-status/${encodeURIComponent(safeTaskId)}/`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Security event generation status failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI security event generation status');
}
